import React from "react";
import { Alert, Typography, Spin, Space, Button } from "antd";
import { DownloadOutlined } from "@ant-design/icons";

import { W0, W1 } from "@components/Constants";
import { ACCOUNT_TAG } from "@api/services/backstage/shapes/OptionGroupEnum";
import { TYPE_UPLOAD, TYPE_HIDDEN, TYPE_TAGS } from "@components/Form";

import downloadFile from "./downloadFile";
import downloadContent from "./downloadContent";

const LABEL_TAGS = "Tags";
const LABEL_FILE = "Import File";
const LABEL_ERRORS = "Validation has failed. Please review ";
const LABEL_REPORT = "validation report";
const LABEL_VALIDATION = "Validating import file...";
const LABEL_DOWNLOAD_TEMPLATE = "Download Import Template";

const REPORT_FILE_TYPE = "applidation/json";
const REPORT_FILE_NAME = "import_validation_report.json";

const ALERT_STYLE = { marginTop: -10 };
const TEMPLATE_STYLE = { marginBottom: W1 };

export type ImportInput = {
  name: string;
  type?: string;
  label?: string;
  required?: boolean;
  component?: () => JSX.Element;
  inputProps?: Record<string, unknown>;
  initialValue?: unknown;
};

const getInputs = (
  templateUrl: string,
  isValidating: boolean,
  errorsJson?: string,
  projectId?: string | null,
  extraInputs: ImportInput[] = [],
): ImportInput[] => {
  const downloadTemplate = (): void =>
    downloadFile(templateUrl);

  const inputs: ImportInput[] = [
    {
      name: "_template",
      component: () => (
        <div style={TEMPLATE_STYLE}>
          <Button
            icon={<DownloadOutlined />}
            type="dashed"
            onClick={downloadTemplate}
          >
            {LABEL_DOWNLOAD_TEMPLATE}
          </Button>
        </div>
      ),
    },
    {
      name: "shouldValidateOnly",
      type: TYPE_HIDDEN,
      initialValue: true,
    },
    {
      name: "importFileUrl",
      type: TYPE_UPLOAD,
      label: LABEL_FILE,
    },
    {
      name: "tagIds",
      type: TYPE_TAGS,
      label: LABEL_TAGS,
      required: false,
      inputProps: {
        group: ACCOUNT_TAG,
      },
    },
  ];

  const hasProjectId = !!projectId;

  if (hasProjectId) {
    inputs.push({
      name: "projectId",
      type: TYPE_HIDDEN,
      initialValue: projectId,
    });
  }

  if (isValidating) {
    inputs.push({
      name: "_validationAlert",
      component: () => (
        <Alert
          type="info"
          style={ALERT_STYLE}
          message={
            <Space size={W0}>
              <Spin size="small" />
              {LABEL_VALIDATION}
            </Space>
          }
        />
      ),
    });
  }

  const hasErrors = !!errorsJson;

  if (hasErrors) {
    const downloadReport = (): void =>
      downloadContent(REPORT_FILE_NAME, errorsJson, REPORT_FILE_TYPE);

    const renderMessage = (): JSX.Element => (
      <>
        {LABEL_ERRORS}
        <Typography.Link onClick={downloadReport}>
          {LABEL_REPORT}
        </Typography.Link>.
      </>
    );

    inputs.push({
      name: "_errorsJson",
      component: () => (
        <div>
          <Alert
            type="error"
            style={ALERT_STYLE}
            message={renderMessage()}
          />
        </div>
      ),
    });
  }

  const allInputs = [
    ...inputs,
    ...extraInputs,
  ];

  return allInputs;
};

export default getInputs;
